import { useState, useEffect } from 'react';
import { Edit2, UserPlus, X } from 'lucide-react';
import api from '../services/api';

export default function UserFormModal({ isOpen, onClose, onSuccess, user }) {
    const show = isOpen;
    const editUser = user;
    const onSave = onSuccess;
    const [fullName, setFullName] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [roleId, setRoleId] = useState('');
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (show) {
            loadRoles();
            if (editUser) {
                setFullName(editUser.full_name);
                setUsername(editUser.username);
                setEmail(editUser.email || '');
                setRoleId(editUser.role_id || '');
            } else {
                resetForm();
            }
        }
    }, [show, editUser]);

    // Gerar sugestão de username ao digitar nome
    useEffect(() => {
        if (fullName && !editUser) {
            const suggested = fullName
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .split(' ')
                .filter(n => n.length > 0)
                .join('.');
            setUsername(suggested);
        }
    }, [fullName, editUser]);

    const loadRoles = async () => {
        try {
            const response = await api.get('/job-roles');
            setRoles(response.data);
        } catch (error) {
            console.error('Erro ao carregar funções:', error);
        }
    };

    const resetForm = () => {
        setFullName('');
        setUsername('');
        setPassword('');
        setEmail('');
        setRoleId('');
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (editUser) {
                // Atualizar usuário
                await api.patch(`/users/${editUser.id}`, {
                    full_name: fullName,
                    email: email || null,
                    role_id: roleId || null
                });
            } else {
                // Criar novo usuário
                if (!password) {
                    setError('Senha é obrigatória para novo usuário');
                    setLoading(false);
                    return;
                }

                await api.post('/users', {
                    username,
                    full_name: fullName,
                    password,
                    email: email || null,
                    role_id: roleId || null
                });
            }

            if (onSave) onSave();
            onClose();
            resetForm();
        } catch (error) {
            console.error('Erro ao salvar usuário:', error);
            setError(error.response?.data?.error || 'Erro ao salvar usuário');
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 dark:border-gray-700 animate-in fade-in zoom-in duration-200">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                        {editUser ? <Edit2 className="w-5 h-5 text-blue-600" /> : <UserPlus className="w-5 h-5 text-green-600" />}
                        {editUser ? 'Editar Usuário' : 'Novo Usuário'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="p-6 space-y-4">
                        {error && (
                            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm p-3 rounded-lg border border-red-100 dark:border-red-900/50">
                                {error}
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <label htmlFor="fullName" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Nome Completo <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="fullName"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                required
                                disabled={loading}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                placeholder="Ex: João da Silva"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="username" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Nome de Usuário <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                disabled={loading || !!editUser}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                placeholder="Ex: joao.silva"
                            />
                            {!editUser && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Sugestão automática baseada no nome
                                </p>
                            )}
                        </div>

                        {!editUser && (
                            <div className="space-y-1.5">
                                <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Senha Inicial <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    disabled={loading}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                    placeholder="••••••"
                                />
                                <p className="text-xs text-amber-600 dark:text-amber-500">
                                    O usuário deverá alterar a senha no primeiro acesso.
                                </p>
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                                placeholder="Ex: joao@hospital.com"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="roleId" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Cargo / Função
                            </label>
                            <select
                                id="roleId"
                                value={roleId}
                                onChange={(e) => setRoleId(e.target.value)}
                                disabled={loading}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                            >
                                <option value="">Sem função específica</option>
                                {roles.map(role => (
                                    <option key={role.id} value={role.id}>{role.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-3 rounded-b-xl">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    Processando...
                                </>
                            ) : (
                                <>
                                    {editUser ? 'Salvar Alterações' : 'Criar Usuário'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
